import { useCallback, useEffect, useRef, useState } from 'react';
import { MapContainer, TileLayer, Polygon, Marker, Tooltip } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';
import 'leaflet-draw';
import { Plus, RotateCcw, Search, Trash2, X } from 'lucide-react';

import trackDozer from '../assets/eq_icons/Track Dozer.svg';
import backhoeLoader from '../assets/eq_icons/Backhoe Loader.svg';
import dumpTruck from '../assets/eq_icons/Articulated Dump Truck.svg';
import dieselBowser from '../assets/eq_icons/Diesel Bowser.svg';
import excavator from '../assets/eq_icons/Excavator.svg';
import grader from '../assets/eq_icons/Motor Grader.svg';
import wheelLoader from '../assets/eq_icons/Wheel Loader.svg';
import pump from '../assets/eq_icons/FEL Water Load.svg';
import wheelDozer from '../assets/eq_icons/Wheel Dozer.svg';
import serviceTruck from '../assets/eq_icons/Service Truck.svg';
import useAutoRefresh from '../hooks/useAutoRefresh';

function randomColor() {
  return '#' + Math.floor(Math.random() * 0xffffff).toString(16).padStart(6, '0');
}

export default function GeofenceMap({ equipment = [] }) {
  const mapRef = useRef(null);
  const hasShownKeyError = useRef(false);
  const [geofences, setGeofences] = useState([]);
  const [selected, setSelected] = useState(null);
  const [drawing, setDrawing] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [newData, setNewData] = useState({ name: '', color: randomColor(), coords: [] });
  const [toast, setToast] = useState('');

  const defaultView = {
    // Default to requested location
    center: [-25.680774, 28.265415],
    zoom: 18,
  };

  const [initialView] = useState(() => {
    try {
      if (typeof window === 'undefined') return defaultView;
      const saved = localStorage.getItem('equipmentMapView');
      if (!saved) return defaultView;
      const parsed = JSON.parse(saved);
      if (
        !parsed ||
        !Array.isArray(parsed.center) ||
        parsed.center.length !== 2 ||
        typeof parsed.zoom !== 'number'
      ) {
        return defaultView;
      }
      return parsed;
    } catch (error) {
      console.warn('Failed to restore map view from localStorage', error);
      return defaultView;
    }
  });

  const [zoom, setZoom] = useState(initialView.zoom);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const geoapifyKey = process.env.REACT_APP_GEOAPIFY_KEY || process.env.VITE_GEOAPIFY_KEY;

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2000);
  };

  const fetchGeofences = useCallback(async () => {
    try {
      const res = await fetch('/api/geofences.php');
      const data = await res.json();
      setGeofences(
        data.map((g) => ({
          ...g,
          coordinates: JSON.parse(g.coordinates),
        }))
      );
    } catch (err) {
      console.error(err);
    }
  }, []);

  useAutoRefresh(fetchGeofences, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) {
      setSearchResults([]);
    }
  };

  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return undefined;
    }

    if (!geoapifyKey) {
      if (!hasShownKeyError.current) {
        showToast('Missing Geoapify API key');
        hasShownKeyError.current = true;
      }
      setSearchResults([]);
      return undefined;
    }

    const controller = new AbortController();
    const timer = setTimeout(async () => {
      try {
        setSearchLoading(true);
        const params = new URLSearchParams({
          text: searchQuery,
          format: 'json',
          limit: '6',
          apiKey: geoapifyKey,
        });
        const res = await fetch(
          `https://api.geoapify.com/v1/geocode/search?${params.toString()}`,
          { signal: controller.signal }
        );
        if (!res.ok) throw new Error('Search failed');
        const data = await res.json();
        const results = Array.isArray(data?.results)
          ? data.results.map((result, index) => ({
              id: result.place_id ?? `${result.lat}-${result.lon}-${index}`,
              label: result.formatted,
              lat: result.lat,
              lon: result.lon,
              raw: result,
            }))
          : [];
        setSearchResults(results);
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error(err);
        }
      } finally {
        setSearchLoading(false);
      }
    }, 250);

    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [searchQuery, geoapifyKey]);

  const selectResult = (result) => {
    const map = mapRef.current;
    if (map) {
      const lat = parseFloat(result.lat);
      const lon = parseFloat(result.lon);
      const newCenter = [lat, lon];
      map.setView(newCenter, 18);
      localStorage.setItem(
        'equipmentMapView',
        JSON.stringify({ center: newCenter, zoom: 18 })
      );
    }
    setSearchQuery(result.label);
    setSearchResults([]);
  };

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const saveView = () => {
      const center = map.getCenter();
      const z = map.getZoom();
      setZoom(z);
      localStorage.setItem(
        'equipmentMapView',
        JSON.stringify({ center: [center.lat, center.lng], zoom: z })
      );
    };
    map.on('moveend', saveView);
    map.on('zoomend', saveView);
    return () => {
      map.off('moveend', saveView);
      map.off('zoomend', saveView);
    };
  }, []);

  const iconMap = {
    'Track Dozer': trackDozer,
    'Backhoe Loader': backhoeLoader,
    'Dump Truck': dumpTruck,
    'Diesel Bowser': dieselBowser,
    Excavator: excavator,
    Grader: grader,
    'Wheel Loader': wheelLoader,
    Pump: pump,
    'Wheel dozer': wheelDozer,
    'Service trucks': serviceTruck,
  };

  const getIcon = (name) => {
    const baseSize = 40;
    const scale = zoom / 40;
    const size = baseSize * scale;
    const url = iconMap[name] || serviceTruck;
    return L.icon({
      iconUrl: url,
      iconSize: [size, size],
      iconAnchor: [size / 2, size / 2],
    });
  };

  const startDrawing = () => {
    const map = mapRef.current;
    if (!map) return;
    window.isEditingGeofence = true;
    const draw = new L.Draw.Polygon(map, { showArea: false });
    draw.enable();
    setDrawing(draw);
    let created = false;
    map.once(L.Draw.Event.CREATED, (e) => {
      created = true;
      draw.disable();
      setDrawing(null);
      const coords = e.layer.getLatLngs()[0].map((ll) => [ll.lat, ll.lng]);
      setNewData({ name: '', color: randomColor(), coords });
      setShowDialog(true);
    });
    map.once(L.Draw.Event.DRAWSTOP, () => {
      if (!created) {
        window.isEditingGeofence = false;
      }
    });
  };

  const undoLast = () => {
    if (drawing) {
      drawing.deleteLastVertex();
    }
  };

  const saveGeofence = () => {
    fetch('/api/geofences.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: newData.name,
        color: newData.color,
        coordinates: JSON.stringify(newData.coords),
      }),
    })
      .then((res) => res.json())
      .then((data) => {
        setGeofences([
          ...geofences,
          { id: data.id, name: newData.name, color: newData.color, coordinates: newData.coords },
        ]);
        setShowDialog(false);
        window.isEditingGeofence = false;
        showToast('Geofence saved');
      })
      .catch(() => showToast('Error saving'));
  };

  const deleteSelected = () => {
    if (!selected) return;
    if (!window.confirm('Delete selected geofence?')) return;
    fetch(`/api/geofences.php?id=${selected}`, { method: 'DELETE' })
      .then((res) => res.json())
      .then(() => {
        setGeofences(geofences.filter((g) => g.id !== selected));
        setSelected(null);
        showToast('Geofence deleted');
      })
      .catch(() => showToast('Delete failed'));
  };

  return (
    <div className="relative flex min-h-0 flex-1 flex-col gap-3 sm:gap-4">
      <div className="relative z-30 flex flex-col gap-3 rounded-3xl border border-white/10 bg-white/10 p-3 shadow-inner shadow-slate-900/10 backdrop-blur-xl dark:border-white/10 dark:bg-slate-900/40 dark:shadow-black/30 md:flex-row md:items-center md:justify-between md:p-4">
        <form onSubmit={handleSearch} className="relative z-40 flex-1 md:max-w-md">
          <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-300">
            <Search className="h-4 w-4" />
          </span>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search for an address, site, or landmark"
            className="h-11 w-full rounded-full border border-white/15 bg-slate-900/70 pl-11 pr-4 text-sm text-slate-100 placeholder:text-slate-400 focus:border-emerald-400/70 focus:outline-none focus:ring-2 focus:ring-emerald-300/50 md:h-12"
          />
          {(searchResults.length > 0 || searchLoading) && (
            <ul className="absolute left-0 right-0 top-full z-[7000] mt-3 overflow-hidden rounded-3xl border border-slate-200 bg-white text-sm text-slate-900 shadow-2xl shadow-slate-900/30">
              {searchLoading && (
                <li className="px-4 py-2 text-xs font-semibold text-slate-500">Searching…</li>
              )}
              {searchResults.map((result) => (
                <li
                  key={result.id}
                  className="cursor-pointer px-4 py-2 transition hover:bg-slate-100"
                  onClick={() => selectResult(result)}
                >
                  {result.label}
                </li>
              ))}
            </ul>
          )}
        </form>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={startDrawing}
            className="inline-flex items-center gap-2 rounded-full border border-emerald-400/70 bg-gradient-to-r from-emerald-400 via-sky-400 to-blue-500 px-3 py-2 text-xs font-semibold text-emerald-950 shadow-lg shadow-emerald-500/30 transition hover:-translate-y-0.5 hover:from-emerald-300 hover:via-sky-300 hover:to-blue-400 focus:outline-none focus:ring-2 focus:ring-emerald-300/60 sm:px-4 sm:text-sm"
          >
            <Plus className="h-4 w-4" /> New geofence
          </button>
          {drawing && (
            <button
              type="button"
              onClick={undoLast}
              className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-2 text-xs font-medium text-white transition hover:border-white/40 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-sky-300/50 sm:px-4 sm:text-sm"
            >
              <RotateCcw className="h-4 w-4" /> Undo point
            </button>
          )}
          <button
            type="button"
            onClick={deleteSelected}
            disabled={!selected}
            className="inline-flex items-center gap-2 rounded-full border border-rose-500/70 bg-rose-500/20 px-3 py-2 text-xs font-semibold text-rose-100 shadow-lg shadow-rose-500/20 transition hover:-translate-y-0.5 hover:bg-rose-500/30 focus:outline-none focus:ring-2 focus:ring-rose-400/70 disabled:cursor-not-allowed disabled:opacity-40 sm:px-4 sm:text-sm"
          >
            <Trash2 className="h-4 w-4" /> Delete geofence
          </button>
        </div>
      </div>

      <div className="relative z-0 flex-1 rounded-[32px] border border-white/10 bg-slate-950/70 p-3 shadow-[0_35px_120px_rgba(15,23,42,0.45)] sm:p-4">
        <div className="pointer-events-none absolute inset-0 rounded-[32px] border border-white/5 opacity-40" />
        <div className="relative h-[18rem] w-full overflow-hidden rounded-[26px] sm:h-[22rem] lg:h-full lg:min-h-[24rem]">
          <MapContainer
            center={initialView.center}
            zoom={initialView.zoom}
            style={{ height: '100%', width: '100%' }}
            ref={mapRef}
            className="h-full w-full"
          >
            <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" />
            {geofences.map((g) => (
              <Polygon
                key={g.id}
                positions={g.coordinates}
                pathOptions={{
                  color: g.color,
                  fillColor: g.color,
                  fillOpacity: 0.4,
                  weight: selected === g.id ? 4 : 2,
                }}
                eventHandlers={{
                  click: () => setSelected(g.id),
                }}
              />
            ))}
            {equipment
              .filter((e) => e.coordinate)
              .map((e, idx) => {
                const [lat, lng] = e.coordinate
                  .split(',')
                  .map((c) => parseFloat(c.trim()));
                return (
                  <Marker
                    key={`eq-${idx}`}
                    position={[lat, lng]}
                    icon={getIcon(e.equipment)}
                    zIndexOffset={1000}
                  >
                    <Tooltip direction="top" opacity={1} permanent={false}>
                      <div className="text-xs">
                        <div>{e.equipment}</div>
                        <div>Operator: {e.operator}</div>
                        <div>Status: {e.current_status}</div>
                        <div>{new Date(e.created_at).toLocaleString()}</div>
                      </div>
                    </Tooltip>
                  </Marker>
                );
              })}
          </MapContainer>
        </div>
      </div>

      {showDialog && (
        <div className="absolute inset-0 z-[2000] flex items-center justify-center bg-slate-950/80 backdrop-blur-sm">
          <div className="relative w-full max-w-md space-y-5 overflow-hidden rounded-3xl border border-white/10 bg-slate-950/90 p-6 text-slate-100 shadow-2xl shadow-slate-950/60">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-white">Save geofence</h3>
                <p className="text-xs text-slate-300">Name and colour-code the new zone for future tracking.</p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setShowDialog(false);
                  window.isEditingGeofence = false;
                }}
                className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/5 text-slate-200 transition hover:border-white/20 hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-emerald-300/60"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="space-y-4">
              <label className="text-xs font-medium uppercase tracking-wide text-slate-300">
                Geofence name
                <input
                  className="mt-2 w-full rounded-2xl border border-white/10 bg-slate-900/60 px-4 py-3 text-sm text-white placeholder:text-slate-400 focus:border-emerald-400/70 focus:outline-none focus:ring-2 focus:ring-emerald-300/50"
                  value={newData.name}
                  onChange={(e) => setNewData({ ...newData, name: e.target.value })}
                  placeholder="e.g. North Pit Safety Zone"
                />
              </label>
              <label className="flex items-center justify-between gap-4 text-xs font-medium uppercase tracking-wide text-slate-300">
                Colour tag
                <input
                  type="color"
                  value={newData.color}
                  onChange={(e) => setNewData({ ...newData, color: e.target.value })}
                  className="h-12 w-20 cursor-pointer rounded-2xl border border-white/10 bg-transparent p-1"
                />
              </label>
            </div>
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowDialog(false);
                  window.isEditingGeofence = false;
                }}
                className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-2 text-sm font-medium text-slate-200 transition hover:border-white/30 hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-sky-300/60"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveGeofence}
                disabled={!newData.name}
                className="inline-flex items-center gap-2 rounded-full border border-emerald-400/70 bg-gradient-to-r from-emerald-400 via-sky-400 to-blue-500 px-5 py-2 text-sm font-semibold text-emerald-950 shadow-lg shadow-emerald-500/30 transition hover:-translate-y-0.5 hover:from-emerald-300 hover:via-sky-300 hover:to-blue-400 focus:outline-none focus:ring-2 focus:ring-emerald-300/60 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Save geofence
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div className="pointer-events-none absolute bottom-4 right-4 z-[1500] rounded-full border border-white/10 bg-slate-950/90 px-4 py-2 text-sm font-medium text-slate-100 shadow-lg shadow-slate-950/50">
          {toast}
        </div>
      )}
    </div>
  );
}